import { addHours, isAfter } from 'date-fns';
import groupsService from 'packages/services/groupsService';
import fontsService from 'packages/services/fontsService';
import trackingService from 'packages/services/trackingService';
import userService from 'packages/services/userService';
import authService from 'packages/services/authService';
import filtersStorage from 'packages/hooks/filtersStorage';
import { catchResponseError } from 'packages/helpers/helpers';
import AuthorizationHelper from '../../helpers/AuthorizationHelper';
import isInIframe from '../../helpers/iframeHelper';
import ProxyServerConnector from 'packages/services/connectors/ProxyServerConnector';
import { updateEditorFonts } from 'packages/store/creativeEditor/creativeEditor.actions';
import * as types from '../types';

const { syncForUser, getFiltersFromStorage, setFiltersToStorage } = filtersStorage();

let refetchFontsIntervalId = -1;
let visibilityChangeHandler = null;

export function getUser() {
    return async (dispatch) => {
        try {
            const { status, json: data } = await userService.getUser();

            if (status === 200 && data) {
                AuthorizationHelper.setPermissions(data.permissions, data.company);
                AuthorizationHelper.setRoles(data.roles);
                AuthorizationHelper.setFeatures(data.accessibleFeatures);

                const currentCompanyData = data.companies.find((company) => company.id === data.company);
                AuthorizationHelper.appcuesIdentify(data.user, currentCompanyData, data.roles, data.accessibleFeatures);
                syncForUser(data.user.bynderUserId || data.user.id);

                dispatch(getUserFonts(data.company));

                dispatch({
                    type: types.LOGIN_SUCCESS,
                    user: data.user,
                    companies: data.companies,
                    currentCompany: data.company,
                    permissions: data.permissions,
                    roles: data.roles,
                    accessibleFeatures: data.accessibleFeatures,
                    bynderScopes: data.bynderScopes,
                });

                dispatch({
                    type: types.TOGGLE_APP_INITIALIZING,
                    state: false,
                });

                const storedUser = getFiltersFromStorage('loginTracking');

                if (storedUser) {
                    if (isAfter(new Date(), addHours(new Date(storedUser.currentDateTime), 1))) {
                        await trackingService.registerLogin({
                            bynderUserId: data.user.bynderUserId,
                            bynderAccountId: data.companies[0].bynderAccountId,
                        });

                        setFiltersToStorage('loginTracking', {
                            bynderUserId: data.user.bynderUserId,
                            bynderAccountId: data.companies[0].bynderAccountId,
                            currentDateTime: new Date(),
                        });
                    }
                } else {
                    await trackingService.registerLogin({
                        bynderUserId: data.user.bynderUserId,
                        bynderAccountId: data.companies[0].bynderAccountId,
                    });

                    setFiltersToStorage('loginTracking', {
                        bynderUserId: data.user.bynderUserId,
                        bynderAccountId: data.companies[0].bynderAccountId,
                        currentDateTime: new Date(),
                    });
                }

                dispatch(getGroups());
            }
        } catch (err) {
            catchResponseError(err);
        }
    };
}

export function loginBynderUser() {
    return (dispatch, getState) => {
        const { portalDomain, bynderAccessToken } = getState().platform;

        if (!bynderAccessToken) {
            console.error('loginBynderUser: No bynder access token.');

            return;
        }

        authService
            .exchangeBynderToIdentityToken({
                portalDomain,
                accessToken: bynderAccessToken,
            })
            .then((authResponse) => {
                if (authResponse.status === 200) {
                    if (authResponse.json.loginUrl) {
                        if (location.pathname !== '/bynder-auth') {
                            const redirectUrl = `${location.pathname}${location.search}`;
                            console.log(`Set url on ${redirectUrl}`);
                            sessionStorage.setItem('redirectUrl', redirectUrl);
                        }

                        if (isInIframe()) {
                            dispatch(sendRedirectCommand(authResponse.json.loginUrl));
                        } else {
                            window.location.href = authResponse.json.loginUrl;
                        }

                        return null;
                    }

                    dispatch({
                        type: types.UPDATE_PLATFORM_OPTIONS,
                        options: {
                            accessToken: authResponse.json.identityAccessToken,
                        },
                    });

                    dispatch(getUser());

                    ProxyServerConnector.flushWaitList();
                }

                return null;
            })
            .catch(catchResponseError);
    };
}

export function registerBynderUser(authCode, state, cb) {
    return async (dispatch) => {
        dispatch({
            type: types.UPDATE_PLATFORM_OPTIONS,
            options: {
                bynderAccessToken: null,
            },
        });

        try {
            const {
                json: { bynderAccessToken },
                status,
            } = await authService.registerBynderUser({ authCode, state });

            if (status === 200) {
                dispatch({
                    type: types.UPDATE_PLATFORM_OPTIONS,
                    options: {
                        bynderAccessToken,
                    },
                });

                if (cb) {
                    cb();
                }
            }
        } catch (err) {
            catchResponseError(err);
        }
    };
}

function getGroups() {
    return (dispatch) => {
        dispatch({ type: types.GET_GROUPS_REQUEST });
        groupsService
            .getGroups()
            .then(({ json }) => {
                dispatch({ type: types.GET_GROUPS_SUCCESS, groups: json.items });
            })
            .catch(catchResponseError);
    };
}

export function getUserFonts(currentCompanyId) {
    return (dispatch) => {
        if (visibilityChangeHandler) {
            document.removeEventListener('visibilitychange', visibilityChangeHandler);
        }

        clearInterval(refetchFontsIntervalId);

        dispatch({ type: types.GET_FONTS_REQUEST });

        const updateFonts = () =>
            fontsService
                .getFonts(currentCompanyId)
                .then(({ json }) => {
                    dispatch({ type: types.GET_FONTS_SUCCESS, items: json });
                    dispatch(updateEditorFonts());
                })
                .catch(catchResponseError);

        updateFonts();
        refetchFontsIntervalId = setInterval(updateFonts, 1000 * 60 * 10);

        // save callback in the upper scope to be able to remove when the function is called again
        visibilityChangeHandler = () => {
            if (document.hidden) {
                // Clear interval when tab becomes inactive
                clearInterval(refetchFontsIntervalId);
            } else {
                // Refetch fonts and create a new interval when tab becomes active
                updateFonts();
                refetchFontsIntervalId = setInterval(updateFonts, 1000 * 60 * 10);
            }
        };

        document.addEventListener('visibilitychange', visibilityChangeHandler);
    };
}

export function logout() {
    return (dispatch, getState) => {
        getState().platform.hostPort.postMessage({
            type: 'logout',
        });
        // clearAllFiltersFromStorage();

        dispatch({ type: types.LOGOUT });
    };
}

export function sendRefreshCommand() {
    return (dispatch, getState) => {
        getState().platform.hostPort.postMessage({
            type: 'refresh_token',
        });
        setFiltersToStorage('loginTracking', {
            ...getFiltersFromStorage('loginTracking'),
            currentDateTime: new Date(),
        });
    };
}

function sendRedirectCommand(url) {
    return (dispatch, getState) => {
        getState().platform.hostPort.postMessage({
            type: 'redirect',
            url,
        });
    };
}
